import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { createStackNavigator } from '@react-navigation/stack';
import { useNavigation, useNavigationState } from '@react-navigation/native';

import SideMenu from './SideMenu';
import { isMusicActive, isMusicMuted, subscribeMusic, toggleMusicMuted } from '../hooks/useMissionMusic';
import MissionsScreen from '../screens/MissionsScreen';
import ChatScreen from '../screens/ChatScreen';
import NotificationsScreen from '../screens/NotificationsScreen';
import ProfileScreen from '../screens/ProfileScreen';
import DashboardScreen from '../screens/DashboardScreen';
import LeaderboardScreen from '../screens/LeaderboardScreen';
import InvoicesScreen from '../screens/InvoicesScreen';
import PaymentMethodsScreen from '../screens/PaymentMethodsScreen';
import CartScreen from '../screens/CartScreen';
import MyInformationScreen from '../screens/MyInformationScreen';
import ChapterDetailScreen from '../screens/ChapterDetailScreen';
import SceneReaderScreen from '../screens/SceneReaderScreen';
import MissionChatScreen from '../screens/MissionChatScreen';
import QuizScreen from '../screens/QuizScreen';
import TaskDetailScreen from '../screens/TaskDetailScreen';
import MissionCardsScreen from '../screens/MissionCardsScreen';
import MissionDetailScreen from '../screens/MissionDetailScreen';
import UsChatScreen from '../screens/UsChatScreen';
import CharacterChatScreen from '../screens/CharacterChatScreen';
import UserStoryDetailScreen from '../screens/UserStoryDetailScreen';
import CompanyAdminScreen from '../screens/company/CompanyAdminScreen';
import CompanyDetailScreen from '../screens/company/CompanyDetailScreen';
import MyInvitationsScreen from '../screens/company/MyInvitationsScreen';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { useMissions } from '../context/MissionsContext';
import { useNotifications } from '../context/NotificationsContext';
import { useT } from '../i18n/useT';
import { openInMission } from '../utils/missionPanel';

const InnerStack = createStackNavigator();

const MISSIONS_DETAIL_SCREENS = [
  'ChapterDetail', 'SceneReader', 'MissionChat', 'Quiz', 'TaskDetail',
  'MissionCards', 'MissionDetail', 'UserStoryDetail',
  // Chat runtime d'une US (hint / refus / validation) — CCT-97 : sans lui, l'écran s'ouvrait
  // HORS du layout desktop (menu + en-tête disparaissaient).
  'UsChat', 'CharacterChat',
];

function InnerNavigator() {
  return (
    <InnerStack.Navigator screenOptions={{ headerShown: false }}>
      <InnerStack.Screen name="Missions"        component={MissionsScreen} />
      <InnerStack.Screen name="Chat"            component={ChatScreen} />
      <InnerStack.Screen name="Dashboard"       component={DashboardScreen} />
      <InnerStack.Screen name="Leaderboard"     component={LeaderboardScreen} />
      <InnerStack.Screen name="Invoices"        component={InvoicesScreen} />
      <InnerStack.Screen name="PaymentMethods"  component={PaymentMethodsScreen} />
      <InnerStack.Screen name="Account"         component={ProfileScreen} />
      <InnerStack.Screen name="MyInformation"   component={MyInformationScreen} />
      <InnerStack.Screen name="Notifications"   component={NotificationsScreen} />
      <InnerStack.Screen name="Cart"            component={CartScreen} />
      <InnerStack.Screen name="ChapterDetail"   component={ChapterDetailScreen} />
      <InnerStack.Screen name="SceneReader"     component={SceneReaderScreen} />
      <InnerStack.Screen name="MissionChat"     component={MissionChatScreen} />
      <InnerStack.Screen name="Quiz"            component={QuizScreen} />
      <InnerStack.Screen name="TaskDetail"      component={TaskDetailScreen} />
      <InnerStack.Screen name="MissionCards"    component={MissionCardsScreen} />
      <InnerStack.Screen name="MissionDetail"   component={MissionDetailScreen} />
      <InnerStack.Screen name="UsChat"          component={UsChatScreen} />
      <InnerStack.Screen name="CharacterChat"   component={CharacterChatScreen} />
      <InnerStack.Screen name="UserStoryDetail" component={UserStoryDetailScreen} />
      <InnerStack.Screen name="CompanyAdmin"    component={CompanyAdminScreen} />
      <InnerStack.Screen name="CompanyDetail"   component={CompanyDetailScreen} />
      <InnerStack.Screen name="MyInvitations"   component={MyInvitationsScreen} />
    </InnerStack.Navigator>
  );
}

function DesktopHeader() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { cartItems } = useMissions();
  const { unread } = useNotifications();

  const currentRouteName = useNavigationState(state => {
    const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
    const innerState = mainRoute?.state;
    if (innerState) {
      return innerState.routes?.[innerState.index ?? 0]?.name ?? 'Missions';
    }
    return 'Missions';
  });

  return (
    <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
      {/* CCT-136 — commande de la MUSIQUE tout en haut à GAUCHE : elle vivait sur le bandeau de
          mission, donc invisible dès qu'un élément était ouvert alors que la piste continuait. */}
      <MusicButton colors={colors} />
      <View style={styles.headerSpacer} />

      {/* CCT-346 — during a mission the notification centre opens OVER it: leaving the screen
          would stop the music and put the open element aside, for a glance at a badge. */}
      <TouchableOpacity
        onPress={() => {
          if (openInMission('Notifications')) return;
          navigation.navigate('Main', { screen: 'Notifications' });
        }}
        style={[styles.headerBtn, currentRouteName === 'Notifications' && { backgroundColor: colors.tabActiveBackground }]}
        activeOpacity={0.7}
      >
        <Ionicons
          name="notifications-outline"
          size={24}
          color={currentRouteName === 'Notifications' ? colors.tabActive : colors.iconPrimary}
        />
        {unread > 0 && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{unread}</Text>
          </View>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        onPress={() => navigation.navigate('Main', { screen: 'Cart' })}
        style={[styles.headerBtn, currentRouteName === 'Cart' && { backgroundColor: colors.tabActiveBackground }]}
        activeOpacity={0.7}
      >
        <Ionicons
          name="cart-outline"
          size={24}
          color={currentRouteName === 'Cart' ? colors.tabActive : colors.iconPrimary}
        />
        {cartItems.length > 0 && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{cartItems.length}</Text>
          </View>
        )}
      </TouchableOpacity>
    </View>
  );
}

/** Bouton son du header : n'apparaît QUE si une piste tourne (sinon il n'a rien à couper). */
function MusicButton({ colors }: { colors: any }) {
  const t = useT();
  const [, force] = useState(0);
  useEffect(() => subscribeMusic(() => force((n) => n + 1)), []);
  if (!isMusicActive()) return null;
  const muted = isMusicMuted();
  return (
    <TouchableOpacity onPress={toggleMusicMuted} style={styles.headerBtn} activeOpacity={0.7}
      accessibilityLabel={t(muted ? 'audio.unmute' : 'audio.mute')}>
      <Ionicons name={muted ? 'volume-mute' : 'volume-high'} size={22}
        color={muted ? colors.iconSecondary : colors.iconPrimary} />
    </TouchableOpacity>
  );
}

export default function DesktopLayout() {
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  return (
    <View style={[styles.wrapper, { backgroundColor: colors.background }]}>
      <DesktopHeader />
      <View style={styles.container}>
        <SideMenu missionDetailScreens={MISSIONS_DETAIL_SCREENS} />
        <View style={[styles.content, { backgroundColor: colors.background }]}>
          <InnerNavigator />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: { flex: 1, flexDirection: 'column' },
  header: { height: 52, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', paddingHorizontal: 16, borderBottomWidth: 1, gap: 4 },
  headerSpacer: { flex: 1 },
  headerBtn: { position: 'relative', padding: 8, borderRadius: 8 },
  badge: { position: 'absolute', top: 4, right: 4, backgroundColor: '#EF4444', borderRadius: 10, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 3 },
  badgeText: { color: '#FFFFFF', fontSize: 11, fontWeight: 'bold' },
  container: { flex: 1, flexDirection: 'row' },
  content: { flex: 1, overflow: 'hidden' },
});
